import { useEffect, useCallback, useState, useRef } from 'react';
import {
  initializeSocket,
  disconnectSocket,
  getSocket,
  isSocketConnected,
  joinLobby,
  leaveLobby,
  subscribeToWager,
  unsubscribeFromWager,
  setPresence,
  type TypedSocket,
  type ServerToClientEvents,
  type PresenceStatus,
} from '../lib/socket';
import { useAuthStore } from '../stores/auth.store';

interface UseSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;

  // Event handlers
  on: <K extends keyof ServerToClientEvents>(
    event: K,
    handler: ServerToClientEvents[K]
  ) => void;
  off: <K extends keyof ServerToClientEvents>(
    event: K,
    handler: ServerToClientEvents[K]
  ) => void;

  // Actions
  connect: () => void;
  disconnect: () => void;
  joinLobby: (gameCode: string) => void;
  leaveLobby: (gameCode: string) => void;
  subscribeToWager: (wagerId: string) => void;
  unsubscribeFromWager: (wagerId: string) => void;
  setPresence: (status: PresenceStatus) => void;
}

/**
 * Hook for Socket.io connection and events
 */
export function useSocket(): UseSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const authStore = useAuthStore();
  const socketRef = useRef<TypedSocket | null>(null);

  // Connect to socket
  const connect = useCallback(() => {
    if (!authStore.token) {
      console.warn('[useSocket] No auth token available');
      return;
    }

    if (isSocketConnected()) {
      setIsConnected(true);
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const socket = initializeSocket({
        token: authStore.token,
        onConnect: () => {
          setIsConnected(true);
          setIsConnecting(false);
          setError(null);
        },
        onDisconnect: (reason) => {
          setIsConnected(false);
          if (reason === 'io server disconnect') {
            // Server disconnected us, might need to reconnect
            console.log('[useSocket] Server disconnected, may need to reconnect');
          }
        },
        onError: (err) => {
          setError(err);
          setIsConnecting(false);
        },
      });

      socketRef.current = socket;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to connect'));
      setIsConnecting(false);
    }
  }, [authStore.token]);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    disconnectSocket();
    socketRef.current = null;
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  // Subscribe to events
  const on = useCallback(
    <K extends keyof ServerToClientEvents>(
      event: K,
      handler: ServerToClientEvents[K]
    ) => {
      const socket = getSocket();
      if (socket) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.on(event, handler as any);
      }
    },
    []
  );

  // Unsubscribe from events
  const off = useCallback(
    <K extends keyof ServerToClientEvents>(
      event: K,
      handler: ServerToClientEvents[K]
    ) => {
      const socket = getSocket();
      if (socket) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.off(event, handler as any);
      }
    },
    []
  );

  // Auto-connect when authenticated
  useEffect(() => {
    if (authStore.isAuthenticated && authStore.token) {
      connect();
    } else {
      disconnect();
    }
  }, [authStore.isAuthenticated, authStore.token, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't disconnect on unmount as other components may still need the socket
      // The socket will be disconnected on logout
    };
  }, []);

  // Update connected state when socket state changes
  useEffect(() => {
    const interval = setInterval(() => {
      const connected = isSocketConnected();
      if (connected !== isConnected) {
        setIsConnected(connected);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isConnected]);

  return {
    isConnected,
    isConnecting,
    error,
    on,
    off,
    connect,
    disconnect,
    joinLobby,
    leaveLobby,
    subscribeToWager,
    unsubscribeFromWager,
    setPresence,
  };
}

/**
 * Hook for subscribing to a specific lobby
 */
export function useLobby(gameCode: string) {
  const { isConnected, on, off, joinLobby, leaveLobby } = useSocket();
  const [members, setMembers] = useState<LobbyMember[]>([]);

  interface LobbyMember {
    userId: string;
    displayName: string;
    avatarUrl?: string;
    gameUsername?: string;
    rating?: number;
    status: PresenceStatus;
  }

  // Join lobby on mount
  useEffect(() => {
    if (isConnected && gameCode) {
      joinLobby(gameCode);
    }

    return () => {
      if (gameCode) {
        leaveLobby(gameCode);
      }
    };
  }, [isConnected, gameCode, joinLobby, leaveLobby]);

  // Handle member events
  useEffect(() => {
    if (!isConnected) return;

    const handleMemberJoined = (data: {
      gameCode: string;
      userId: string;
      displayName: string;
      avatarUrl?: string;
      gameUsername?: string;
      rating?: number;
      status: PresenceStatus;
    }) => {
      if (data.gameCode !== gameCode) return;

      setMembers((prev) => {
        // Remove if already exists
        const filtered = prev.filter((m) => m.userId !== data.userId);
        return [...filtered, data];
      });
    };

    const handleMemberLeft = (data: { gameCode: string; userId: string }) => {
      if (data.gameCode !== gameCode) return;
      setMembers((prev) => prev.filter((m) => m.userId !== data.userId));
    };

    const handlePresenceUpdate = (data: {
      userId: string;
      status: PresenceStatus;
      gameCode?: string;
    }) => {
      if (data.gameCode && data.gameCode !== gameCode) return;

      setMembers((prev) =>
        prev.map((m) =>
          m.userId === data.userId ? { ...m, status: data.status } : m
        )
      );
    };

    on('lobby:member_joined', handleMemberJoined);
    on('lobby:member_left', handleMemberLeft);
    on('lobby:presence_update', handlePresenceUpdate);

    return () => {
      off('lobby:member_joined', handleMemberJoined);
      off('lobby:member_left', handleMemberLeft);
      off('lobby:presence_update', handlePresenceUpdate);
    };
  }, [isConnected, gameCode, on, off]);

  return {
    members,
    isConnected,
  };
}

/**
 * Hook for subscribing to wager updates
 */
export function useWagerSubscription(wagerId: string | null) {
  const { isConnected, subscribeToWager, unsubscribeFromWager } = useSocket();

  useEffect(() => {
    if (isConnected && wagerId) {
      subscribeToWager(wagerId);
    }

    return () => {
      if (wagerId) {
        unsubscribeFromWager(wagerId);
      }
    };
  }, [isConnected, wagerId, subscribeToWager, unsubscribeFromWager]);

  return { isConnected };
}
